'use client';

import { useState } from 'react';

import type { PreguntaLanding } from '@/content/faqsLanding';

interface PropiedadesAcordeon {
    preguntas: PreguntaLanding[];
    /** Prefijo de los ids de botón y región: dos acordeones en la misma página no deben chocar. */
    idBase: string;
    /** `landing` resalta la abierta (Landing_FAQ); `pagina` mantiene el fondo blanco en todas. */
    variante: 'landing' | 'pagina';
    /** `grande` es la pregunta de 17 px de la landing; `normal`, la de 16 px del resto. */
    tamanoPregunta?: 'grande' | 'normal';
    /**
     * Fondo de las preguntas en la variante `pagina`. Por defecto `blanco`; Pagina_Precios pinta las suyas
     * en `suave` porque su banda de preguntas ya es blanca y las tarjetas se perderían sobre ella.
     */
    fondoPagina?: 'blanco' | 'suave';
    /**
     * `compacta` es la densidad móvil de Pagina_Poderes_Chat_Movil: filas de 52, relleno 14 y respuesta
     * de 14. Landing_FAQ, Pagina_Precios y Pagina_Funcionalidad mantienen en móvil 56 / 18 / 15, así
     * que es opcional. Desde `lg` las dos densidades son idénticas.
     */
    densidadMovil?: 'normal' | 'compacta';
}

const TAMANOS_PREGUNTA = {
    grande: 'text-[15px] lg:text-[17px]',
    normal: 'text-[15px] lg:text-ds-400',
} as const;

const DENSIDADES = {
    normal: {
        boton: 'min-h-14 px-[18px]',
        respuesta: 'px-[18px] pb-ds-4 text-[15px] leading-[1.6]',
    },
    compacta: {
        boton: 'min-h-[52px] px-[14px] lg:min-h-14 lg:px-[18px]',
        respuesta: 'px-[14px] pb-[14px] text-[14px] leading-[1.55] lg:px-[18px] lg:pb-ds-4 lg:text-[15px] lg:leading-[1.6]',
    },
} as const;

function fondoDe(
    variante: PropiedadesAcordeon['variante'],
    abierta: boolean,
    fondoPagina: NonNullable<PropiedadesAcordeon['fondoPagina']>,
): string {
    if (variante === 'pagina') return fondoPagina === 'suave' ? 'bg-surface-suave' : 'bg-surface-2';
    return abierta ? 'bg-tint-accent shadow-ds-2' : 'bg-surface-suave';
}

interface PropiedadesItem {
    pregunta: PreguntaLanding;
    id: string;
    abierta: boolean;
    clasesFondo: string;
    clasesPregunta: string;
    densidad: (typeof DENSIDADES)[keyof typeof DENSIDADES];
    alPulsar: () => void;
}

function ItemAcordeon({ pregunta, id, abierta, clasesFondo, clasesPregunta, densidad, alPulsar }: PropiedadesItem) {
    const idBoton = `${id}-pregunta`;
    const idRespuesta = `${id}-respuesta`;
    return (
        <div data-pregunta className={`overflow-hidden rounded-ds-lg border-2 border-ink ${clasesFondo}`}>
            <button
                type="button"
                id={idBoton}
                aria-expanded={abierta}
                aria-controls={idRespuesta}
                onClick={alPulsar}
                className={`flex w-full items-center justify-between gap-ds-3 bg-transparent py-[10px] text-left font-sans font-bold text-ink ${densidad.boton} ${clasesPregunta}`}
            >
                {pregunta.pregunta}
                <span aria-hidden="true" className="shrink-0 text-[20px] leading-none">
                    {abierta ? '−' : '+'}
                </span>
            </button>
            <div
                id={idRespuesta}
                role="region"
                aria-labelledby={idBoton}
                hidden={!abierta}
                className={`text-ink/90 ${densidad.respuesta}`}
            >
                {pregunta.respuesta}
            </div>
        </div>
    );
}

/**
 * El acordeón de preguntas frecuentes del lienzo 2.0 (UI-SPEC §Interaction 6). La primera
 * empieza abierta y solo hay una abierta a la vez, pero todas pueden cerrarse. Las respuestas
 * cerradas no se desmontan: van con `hidden`, para que el HTML del servidor contenga las
 * mismas respuestas que el FAQPage JSON-LD. El cambio es instantáneo, como en el lienzo.
 */
export default function Acordeon({
    preguntas,
    idBase,
    variante,
    tamanoPregunta,
    fondoPagina = 'blanco',
    densidadMovil = 'normal',
}: PropiedadesAcordeon) {
    const [abierta, setAbierta] = useState(0);
    const clasesPregunta = TAMANOS_PREGUNTA[tamanoPregunta ?? (variante === 'landing' ? 'grande' : 'normal')];
    const densidad = DENSIDADES[densidadMovil];

    return (
        <div data-acordeon className="flex flex-col gap-[10px]">
            {preguntas.map((pregunta, indice) => (
                <ItemAcordeon
                    key={pregunta.pregunta}
                    pregunta={pregunta}
                    id={`${idBase}-${indice}`}
                    abierta={indice === abierta}
                    clasesFondo={fondoDe(variante, indice === abierta, fondoPagina)}
                    clasesPregunta={clasesPregunta}
                    densidad={densidad}
                    alPulsar={() => setAbierta(indice === abierta ? -1 : indice)}
                />
            ))}
        </div>
    );
}
